<template>
  <img
      class="side-figure"
      :class="`is-variant-${variantIndex}`"
      :src="src"
      alt=""
      aria-hidden="true"
      draggable="false"
      @error="onError"
  />
</template>

<script>
import {CharacterInfoGameService} from '@/services/characterInfoService.js';
import {avatarPlaceholderUrl} from '@/utils/avatarPlaceholder';

/*
 * One side's champion, standing at the outer edge of its sector.
 *
 * The fight clock names participants but carries no portrait, so the picture
 * is fetched by character id through `CharacterInfoGameService` — a service,
 * never a generated API class. A lead the viewer cannot read (an NPC the
 * server will not describe to a player) resolves to the same placeholder the
 * backend's own avatar field defaults to, so the sector still has a figure in
 * it rather than a hole.
 */
export default {
  name: 'FightSideFigure',
  props: {
    characterId: {
      type: String,
      default: null,
    },
    /* The side's slot. Two sides can be led by characters with no portrait
       yet, and the placeholder is one picture — five identical silhouettes
       around one arena reads as a mistake. Framing is varied by slot so each
       figure is at least its own body, facing its own way. */
    variant: {
      type: Number,
      default: 1,
    },
  },
  computed: {
    variantIndex() {
      return ((this.variant ?? 1) - 1 + 5) % 5;
    },
  },
  data() {
    return {
      src: avatarPlaceholderUrl(),
    };
  },
  watch: {
    characterId: {
      immediate: true,
      handler: 'load',
    },
  },
  methods: {
    async load() {
      const placeholder = avatarPlaceholderUrl();
      if (!this.characterId) {
        this.src = placeholder;
        return;
      }
      try {
        const url = await CharacterInfoGameService.getAvatarUrl(this.characterId, false);
        this.src = url || placeholder;
      } catch {
        this.src = placeholder;
      }
    },
    onError() {
      this.src = avatarPlaceholderUrl();
    },
  },
};
</script>

<style scoped>
/* The portrait is a card crop on a flat backdrop, so it is faded into the
   scene from every edge rather than pasted onto it: the mask kills the plate
   the artwork ships with, and the figure reads as part of the environment. */
.side-figure {
  display: block;
  width: 100%;
  height: auto;
  object-fit: contain;
  -webkit-mask-image:
      linear-gradient(to bottom, transparent 0%, #000 16%, #000 62%, transparent 96%),
      radial-gradient(ellipse 62% 70% at 50% 46%, #000 40%, transparent 78%);
  mask-image:
      linear-gradient(to bottom, transparent 0%, #000 16%, #000 62%, transparent 96%),
      radial-gradient(ellipse 62% 70% at 50% 46%, #000 40%, transparent 78%);
  -webkit-mask-composite: source-in;
  mask-composite: intersect;
}

/* Slot framing: which way the figure faces, how close it stands, how high it
   sits in its sector. Purely presentational — the picture is whatever the
   character's own avatar is. */
.is-variant-0 { transform: scale(1); }
.is-variant-1 { transform: scaleX(-1) scale(1.06); }
.is-variant-2 { transform: scale(0.94) translateY(2%); }
.is-variant-3 { transform: scaleX(-1) scale(0.9) translateY(-2%); }
.is-variant-4 { transform: scale(1.1) translateY(3%); }
</style>
